"use client";

/**
 * La vitrine gestic.fr (route `/`, rendue par `app/page.tsx` qui porte le SEO).
 *
 * Direction : un récit à la première personne. « J'en avais marre de n'avoir
 * aucune vision sur certains aspects de ma vie, alors j'ai fabriqué l'app. »
 * Chaque carte de module s'ouvre sur la question qui manquait de réponse.
 *
 * Trois inspirations fondues :
 *   - Revolut  : titres énormes, cartes produit qui flottent.
 *   - Discord  : chaleur, rondeurs, ton direct, bandeau final franc.
 *   - Pennylane: rythme de sections lisible, mosaïque de modules encadrés.
 *
 * Le tout tenu par l'identité de l'app : Fraunces, crème et vert forêt, clair
 * ET sombre, et le motif du logo — le trait plein du réalisé qui se prolonge
 * en pointillés pour le prévu. La carte « à venir » est donc en pointillés.
 *
 * Prix repris de `lib/stripe/plans.ts`, formules et FAQ de `landing-content.ts`
 * (la FAQ affichée doit rester celle du JSON-LD). Les montants des maquettes
 * sont des exemples.
 */

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { APP_VERSION } from "@/lib/changelog";
import { LANDING } from "@/app/landing-content";
import { GESTIC_PLANS, stripeFeesCents } from "@/lib/stripe/plans";

const eur = (cents: number) => (cents / 100).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

/* Couleurs d'identité des modules et des catégories de la maquette budget.
   Palette passée au validateur (clair sur #FBF6EA, sombre sur #232323) :
   vert → bleu → terre → violet, aucune paire adjacente sous le seuil. */
const CSS = `
.vt {
  --vt-fond: #F3EAD6;
  --vt-fond-2: #EFE4CC;
  --vt-carte: #FBF6EA;
  --vt-encre: #22201C;
  --vt-encre-2: #5C574E;
  --vt-vert: #2D6A4F;
  --vt-vert-clair: #40916C;
  --vt-sauge: #A8C2B1;
  --vt-terre: #C1694F;
  --vt-bord: rgb(60 40 15 / .12);
  --vt-ombre: 0 1px 2px rgb(60 40 15 / .06), 0 12px 28px -14px rgb(60 40 15 / .28), 0 32px 60px -30px rgb(60 40 15 / .3);
  --vt-c-vert: #2E8B57;
  --vt-c-bleu: #3A6FC4;
  --vt-c-terre: #C4583A;
  --vt-c-violet: #8A5CC8;
  --vt-c-ambre: #B07D14;
  background: var(--vt-fond);
  color: var(--vt-encre);
}
.dark .vt {
  --vt-fond: #141414;
  --vt-fond-2: #1B1B1B;
  --vt-carte: #232323;
  --vt-encre: #F1ECE1;
  --vt-encre-2: #A9A296;
  --vt-vert: #40916C;
  --vt-vert-clair: #52B788;
  --vt-sauge: #6E8F7B;
  --vt-terre: #D4896F;
  --vt-bord: rgb(255 255 255 / .1);
  --vt-ombre: 0 1px 2px rgb(0 0 0 / .5), 0 14px 30px -14px rgb(0 0 0 / .7), 0 40px 70px -30px rgb(0 0 0 / .8);
  --vt-c-vert: #3AA56B;
  --vt-c-bleu: #5A8EE0;
  --vt-c-terre: #D5663F;
  --vt-c-violet: #A27BE0;
  --vt-c-ambre: #B8871F;
}

.vt-titre { font-family: var(--font-serif); font-weight: 600; letter-spacing: -0.02em; line-height: 1.02; }
.vt-recit { font-family: var(--font-recit), var(--font-serif); font-style: italic; font-weight: 400; letter-spacing: -0.01em; }
.vt-sur { font-size: 11px; font-weight: 700; letter-spacing: .14em; text-transform: uppercase; color: var(--vt-vert); }
.vt-doux { color: var(--vt-encre-2); }

.vt-carte { background: var(--vt-carte); border: 1px solid var(--vt-bord); box-shadow: var(--vt-ombre); }

.vt-bouton {
  display: inline-flex; align-items: center; gap: 8px; border-radius: 999px;
  padding: 14px 24px; font-weight: 600; font-size: 15px;
  background: var(--vt-vert); color: #FBF6EA;
  box-shadow: 0 10px 24px -12px rgb(45 106 79 / .9);
  transition: transform .18s cubic-bezier(.34,1.56,.64,1), box-shadow .25s ease, background .2s ease;
}
.vt-bouton:hover { transform: translateY(-2px); background: var(--vt-vert-clair); box-shadow: 0 16px 30px -12px rgb(45 106 79 / .95); }
.vt-bouton-2 {
  display: inline-flex; align-items: center; gap: 8px; border-radius: 999px;
  padding: 13px 22px; font-weight: 600; font-size: 15px;
  color: var(--vt-encre); border: 1px solid var(--vt-bord); background: transparent;
  transition: transform .18s cubic-bezier(.34,1.56,.64,1), border-color .2s ease, background .2s ease;
}
.vt-bouton-2:hover { transform: translateY(-2px); border-color: color-mix(in srgb, var(--vt-vert) 55%, transparent); background: color-mix(in srgb, var(--vt-vert) 8%, transparent); }

/* Le motif de la marque : le trait plein se prolonge en pointillés. */
.vt-trace { stroke-dasharray: 1; stroke-dashoffset: 1; }
[data-vu="oui"] .vt-trace { animation: vt-trace 1.6s cubic-bezier(.45,0,.2,1) forwards; animation-delay: var(--d, 0s); }
[data-vu="oui"] .vt-trace-2 { --d: .9s; }
@keyframes vt-trace { to { stroke-dashoffset: 0; } }

/* L'entrée anime translate, pas transform : le survol des cartes garde transform. */
.vt-monte { opacity: 0; translate: 0 18px; }
[data-vu="oui"] .vt-monte { animation: vt-monte .7s cubic-bezier(.22,1,.36,1) forwards; animation-delay: calc(var(--i, 0) * 90ms); }
@keyframes vt-monte { to { opacity: 1; translate: 0 0; } }

.vt-flotte { animation: vt-flotte 7s ease-in-out infinite; }
@keyframes vt-flotte { 0%, 100% { transform: translateY(0) } 50% { transform: translateY(-10px) } }

.vt-lueur { position: absolute; border-radius: 999px; filter: blur(70px); opacity: .5; pointer-events: none; }

/* ── Cartes de modules ── */
.vt-module {
  position: relative; border-radius: 28px; background: var(--vt-carte);
  border: 1px solid var(--vt-bord); box-shadow: var(--vt-ombre);
  transition: transform .35s cubic-bezier(.22,1,.36,1), border-color .3s ease;
}
.vt-module:hover { transform: translateY(-4px); border-color: color-mix(in srgb, var(--acc) 40%, var(--vt-bord)); }
/* Le filet coloré en tête, comme les sections de l'app. */
.vt-filet {
  position: absolute; left: 28px; right: 28px; top: -1px; height: 2px; border-radius: 0 0 3px 3px;
  background: linear-gradient(90deg, transparent, var(--acc) 22%, var(--acc) 78%, transparent);
  opacity: .8; transition: height .3s ease, opacity .3s ease;
}
.vt-module:hover .vt-filet { height: 4px; opacity: 1; }
.vt-icone {
  width: 38px; height: 38px; border-radius: 12px; display: grid; place-items: center; flex: none;
  color: var(--acc); background: color-mix(in srgb, var(--acc) 14%, transparent);
}
/* À venir : le prévu est en pointillés, dans l'app comme ici. */
.vt-avenir {
  border-radius: 28px; border: 2px dashed color-mix(in srgb, var(--vt-vert) 45%, transparent);
  background: color-mix(in srgb, var(--vt-carte) 45%, transparent);
}

/* Barres qui poussent depuis la gauche à l'entrée. */
.vt-pousse { transform-origin: left center; transform: scaleX(0); }
[data-vu="oui"] .vt-pousse { animation: vt-pousse .9s cubic-bezier(.22,1,.36,1) forwards; animation-delay: calc(.25s + var(--i, 0) * 80ms); }
@keyframes vt-pousse { to { transform: scaleX(1); } }

/* Anneau des heures d'intermittence : s'arrête à --fin. */
.vt-anneau { stroke-dasharray: 1; stroke-dashoffset: 1; }
[data-vu="oui"] .vt-anneau { animation: vt-anneau 1.4s cubic-bezier(.45,0,.2,1) .3s forwards; }
@keyframes vt-anneau { to { stroke-dashoffset: var(--fin); } }

/* Pro / Light : le contenu change en fondu. */
.vt-bascule { animation: vt-bascule .35s ease; }
@keyframes vt-bascule { from { opacity: 0; translate: 0 6px; } to { opacity: 1; translate: 0 0; } }

@media (prefers-reduced-motion: reduce) {
  .vt-monte, .vt-trace, .vt-pousse, .vt-anneau, .vt-bascule { animation: none !important; opacity: 1; translate: 0 0; transform: none; stroke-dashoffset: 0; }
  .vt-anneau { stroke-dashoffset: var(--fin); }
  .vt-flotte { animation: none; }
  .vt-bouton, .vt-bouton-2, .vt-module { transition: none; }
}
`;

/** Vrai dès que le bloc entre à l'écran : les entrées s'animent une fois. */
function useVu<T extends Element>() {
  const ref = useRef<T>(null);
  const [vu, setVu] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || vu) return;
    if (typeof IntersectionObserver === "undefined") {
      const image = requestAnimationFrame(() => setVu(true));
      return () => cancelAnimationFrame(image);
    }
    const obs = new IntersectionObserver((e) => {
      if (e[e.length - 1]?.isIntersecting) { setVu(true); obs.disconnect(); }
    }, { rootMargin: "0px 0px -80px 0px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [vu]);
  return [ref, vu] as const;
}

function Section({ children, id, fond = false, className = "" }: { children: React.ReactNode; id?: string; fond?: boolean; className?: string }) {
  const [ref, vu] = useVu<HTMLElement>();
  return (
    <section
      id={id}
      ref={ref}
      data-vu={vu ? "oui" : "non"}
      className={`relative px-5 sm:px-8 py-20 sm:py-28 scroll-mt-16 ${className}`}
      style={fond ? { background: "var(--vt-fond-2)" } : undefined}
    >
      <div className="mx-auto max-w-6xl">{children}</div>
    </section>
  );
}

/* ══════════════════════════════════════════
   Icônes des modules (trait, 24×24)
   ══════════════════════════════════════════ */
const TRACES: Record<string, string> = {
  budget: "M3 7.5A2.5 2.5 0 0 1 5.5 5H18v4M3 7.5V17a2 2 0 0 0 2 2h15V9H5.5A2.5 2.5 0 0 1 3 7.5ZM16.5 14h.01",
  patrimoine: "M3 11.5 12 4l9 7.5M5 10v10h5v-5.5h4V20h5V10",
  intermittence: "M4 7h16v3a2 2 0 0 0 0 4v3H4v-3a2 2 0 0 0 0-4V7Zm9 0v10",
  voiture: "M4 16.5h16M6 16.5 7.6 11h8.8l1.6 5.5M4 16.5V19h2.5v-2.5m11 0V19H20v-2.5M7.6 11 9 7h6l1.4 4",
  famille: "M12 3.5v4.5m0 0H6.5v3.5M12 8h5.5v3.5M4 11.5h5v5H4zm11 0h5v5h-5zM6.5 16.5V20m11-3.5V20",
  music: "M9 18V6l11-2v12M9 18a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm11-2a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z",
  clients: "M4 8h16v11H4zM9 8V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V8M4 13h16",
  velo: "M5.5 18.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm13 0a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7ZM5.5 15l3.5-7h6l3.5 7M9 8l3 7h-6.5M13.5 5H16l1 3",
};

function Icone({ nom, className = "w-5 h-5" }: { nom: keyof typeof TRACES; className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d={TRACES[nom]} />
    </svg>
  );
}

/** Le vrai logo : l'icône de l'app, dans sa version claire ou sombre. */
function Logo({ taille = 36 }: { taille?: number }) {
  const style = { width: taille, height: taille };
  return (
    <span className="relative shrink-0 rounded-[10px] overflow-hidden" style={{ ...style, boxShadow: "0 0 0 1px var(--vt-bord), 0 4px 12px -6px rgb(0 0 0 / .35)" }}>
      <Image src="/icon-192.png" alt="" width={taille} height={taille} className="dark:hidden" style={style} priority />
      <Image src="/icon-192-dark.png" alt="" width={taille} height={taille} className="hidden dark:block" style={style} priority />
    </span>
  );
}

/** Bouton d'abonnement : crée la session Stripe Checkout et y redirige. */
function BoutonEssai({ plan, libelle, className }: { plan: "pro" | "light"; libelle: string; className: string }) {
  const [chargement, setChargement] = useState(false);
  async function lancer() {
    setChargement(true);
    try {
      const res = await fetch("/api/stripe/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan }),
      });
      const data = await res.json();
      if (!res.ok || !data.url) {
        alert(data.error ?? "Paiement momentanément indisponible — réessaie dans un instant.");
        setChargement(false);
        return;
      }
      window.location.href = data.url;
    } catch {
      alert("Paiement momentanément indisponible — réessaie dans un instant.");
      setChargement(false);
    }
  }
  return (
    <button type="button" onClick={lancer} disabled={chargement} className={`${className} disabled:opacity-60`}>
      {chargement ? "Redirection vers Stripe…" : libelle}
      {!chargement && <span aria-hidden>→</span>}
    </button>
  );
}

function Coche() {
  return (
    <svg className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--vt-vert)" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.6} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

/* ══════════════════════════════════════════
   La courbe de la marque : réalisé puis prévu
   ══════════════════════════════════════════ */
function Courbe({ className = "", hauteur = 150 }: { className?: string; hauteur?: number }) {
  return (
    <svg viewBox="0 0 1200 200" preserveAspectRatio="none" className={className} style={{ height: hauteur }} aria-hidden>
      <path className="vt-trace" pathLength={1} d="M0 168C120 168 180 150 300 120C400 95 470 60 560 44"
        fill="none" stroke="var(--vt-vert)" strokeWidth={3} strokeLinecap="round" />
      <path className="vt-trace vt-trace-2" pathLength={1} d="M560 44C660 26 760 60 860 72C960 84 1080 50 1200 18"
        fill="none" stroke="var(--vt-vert)" strokeWidth={3} strokeLinecap="round" strokeDasharray="1 10" opacity={0.75} />
    </svg>
  );
}

/* ══════════════════════════════════════════
   Hero : le récit, et « ma vie en un coup d'œil »
   ══════════════════════════════════════════ */
function MaVie() {
  const lignes: { nom: keyof typeof TRACES; acc: string; titre: string; detail: string; valeur: React.ReactNode }[] = [
    { nom: "budget", acc: "var(--vt-c-vert)", titre: "Budget", detail: "solde prévu fin septembre", valeur: "1 714,68 €" },
    { nom: "patrimoine", acc: "var(--vt-c-ambre)", titre: "Patrimoine", detail: "financier + corporel", valeur: "186 450 €" },
    {
      nom: "intermittence", acc: "var(--vt-c-violet)", titre: "Intermittence", detail: "heures acquises",
      valeur: (
        <span className="flex flex-col items-end gap-1">
          <span>412 / 507 h</span>
          <span className="block w-20 h-1.5 rounded-full overflow-hidden" style={{ background: "color-mix(in srgb, var(--vt-encre) 10%, transparent)" }}>
            <span className="vt-pousse block h-full rounded-full" style={{ width: "81%", background: "var(--vt-c-violet)" }} />
          </span>
        </span>
      ),
    },
    { nom: "voiture", acc: "var(--vt-c-bleu)", titre: "Voiture", detail: "prochaine vidange", valeur: "dans 1 200 km" },
    { nom: "famille", acc: "var(--vt-c-terre)", titre: "Famille", detail: "personnes dans l'arbre", valeur: "312" },
  ];
  return (
    <div className="vt-carte rounded-[24px] overflow-hidden w-full">
      <span aria-hidden className="block h-[3px]" style={{ background: "linear-gradient(90deg, transparent, var(--vt-c-vert) 15%, var(--vt-c-ambre) 38%, var(--vt-c-violet) 60%, var(--vt-c-bleu) 80%, transparent)" }} />
      <div className="px-5 pt-4 pb-3">
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-bold uppercase tracking-wider vt-doux">Ma vie, ce mois-ci</p>
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "color-mix(in srgb, var(--vt-vert) 14%, transparent)", color: "var(--vt-vert)" }}>à jour</span>
        </div>
        <ul className="mt-2 divide-y" style={{ borderColor: "var(--vt-bord)" }}>
          {lignes.map((l, i) => (
            <li key={l.titre} className="vt-monte flex items-center justify-between gap-4 py-2.5" style={{ "--i": i + 1, "--acc": l.acc } as React.CSSProperties}>
              <span className="flex items-center gap-3 min-w-0">
                <span className="vt-icone !w-8 !h-8 !rounded-[10px]"><Icone nom={l.nom} className="w-4 h-4" /></span>
                <span className="min-w-0">
                  <span className="block text-[13px] font-semibold leading-tight">{l.titre}</span>
                  <span className="block text-[11px] vt-doux leading-tight">{l.detail}</span>
                </span>
              </span>
              <span className="text-[14px] font-semibold tabular-nums shrink-0 text-right">{l.valeur}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Hero() {
  const [ref, vu] = useVu<HTMLElement>();
  return (
    <section id="histoire" ref={ref} data-vu={vu ? "oui" : "non"} className="relative overflow-hidden px-5 sm:px-8 pt-14 sm:pt-20 pb-20 sm:pb-24">
      <span aria-hidden className="vt-lueur" style={{ width: 520, height: 520, background: "var(--vt-sauge)", top: -220, right: "-6%" }} />
      <span aria-hidden className="vt-lueur" style={{ width: 380, height: 380, background: "var(--vt-terre)", opacity: .22, bottom: -200, left: "-8%" }} />
      <Courbe className="absolute inset-x-0 bottom-0 w-full opacity-[.3]" hauteur={220} />

      <div className="relative mx-auto max-w-6xl grid lg:grid-cols-[minmax(0,1.1fr)_minmax(0,.9fr)] gap-12 lg:gap-16 items-center">
        <div>
          <p className="vt-monte vt-recit text-[22px] sm:text-[27px] leading-snug vt-doux max-w-xl">
            « Moi, j&apos;en avais marre de n&apos;avoir aucune vision sur certains aspects de ma vie. »
          </p>
          <h1 className="vt-monte vt-titre text-[44px] sm:text-[66px] mt-6" style={{ "--i": 1 } as React.CSSProperties}>
            Alors j&apos;ai fabriqué l&apos;app{" "}
            <span className="vt-recit" style={{ color: "var(--vt-vert)", fontWeight: 500 }}>all-rounder</span>{" "}
            de ma vie.
          </h1>
          <p className="vt-monte mt-6 text-[18px] vt-doux leading-relaxed max-w-xl" style={{ "--i": 2 } as React.CSSProperties}>
            Elle ressemble sûrement à la tienne, de près ou de loin : un budget à tenir, des choses qui ont de la valeur, une voiture à entretenir, une famille à raconter. Tout au même endroit, et tout se recoupe.
          </p>
          <div className="vt-monte mt-8 flex flex-wrap gap-3" style={{ "--i": 3 } as React.CSSProperties}>
            <a href="#modules" className="vt-bouton">
              Voir ce qu&apos;elle fait
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden><path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14m0 0-6-6m6 6 6-6" /></svg>
            </a>
            <a href="#pricing" className="vt-bouton-2">Essayer 14 jours</a>
          </div>
          <p className="vt-monte mt-8 flex items-center gap-3 text-[13px] vt-doux" style={{ "--i": 4 } as React.CSSProperties}>
            <span aria-hidden className="w-8 h-px" style={{ background: "var(--vt-encre-2)" }} />
            Le créateur de Gestic — fait en France, sans pub ni investisseurs
          </p>
        </div>

        {/* Toute une vie sur une carte, et des bouts de modules qui flottent autour. */}
        <div className="relative vt-monte lg:pl-4" style={{ "--i": 2 } as React.CSSProperties}>
          <div className="vt-flotte"><MaVie /></div>
          <div className="absolute -top-16 left-4 hidden sm:block vt-flotte" style={{ animationDelay: ".7s" }}>
            <Pastille acc="var(--vt-c-terre)" titre="Week-end à Étretat" detail="journal · 38 photos" />
          </div>
          <div className="absolute -bottom-16 right-2 hidden sm:block vt-flotte" style={{ animationDelay: "1.4s" }}>
            <Pastille acc="var(--vt-c-ambre)" titre="Vinyles" detail="214 disques · 4 430 €" />
          </div>
          <div className="absolute -bottom-16 left-4 hidden sm:block vt-flotte" style={{ animationDelay: "2.1s" }}>
            <Pastille acc="var(--vt-c-vert)" titre="Tâches de la semaine" detail="5 sur 7 faites" />
          </div>
        </div>
      </div>
    </section>
  );
}

function Pastille({ acc, titre, detail }: { acc: string; titre: string; detail: string }) {
  return (
    <div className="vt-carte rounded-2xl pl-3 pr-4 py-2.5 flex items-center gap-2.5">
      <span aria-hidden className="w-2 h-8 rounded-full" style={{ background: acc }} />
      <span>
        <span className="block text-[12.5px] font-semibold leading-tight">{titre}</span>
        <span className="block text-[11px] vt-doux leading-tight mt-0.5">{detail}</span>
      </span>
    </div>
  );
}

/* ══════════════════════════════════════════
   Les cartes de modules
   ══════════════════════════════════════════ */
function Module({ nom, acc, titre, question, i, className = "", children }: {
  nom: keyof typeof TRACES;
  acc: string;
  titre: string;
  /** La question sans réponse qui a fait naître le module. */
  question: string;
  i: number;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <article className={`vt-monte vt-module p-6 sm:p-7 flex flex-col ${className}`} style={{ "--i": i, "--acc": acc } as React.CSSProperties}>
      <span aria-hidden className="vt-filet" />
      <div className="flex items-center gap-3">
        <span className="vt-icone"><Icone nom={nom} /></span>
        <h3 className="text-[17px] font-semibold">{titre}</h3>
      </div>
      {/* Espaces fines insécables : le « ? » ne part jamais seul à la ligne. */}
      <p className="vt-recit mt-5 text-[22px] sm:text-[24px] leading-tight">«&#8239;{question.replace(/ ([?!:;])/g, " $1")}&#8239;»</p>
      {children}
    </article>
  );
}

const CATEGORIES = [
  { nom: "Logement", c: "var(--vt-c-vert)", centimes: 82000, moyenne: "≈ 820 €", tendance: "stable" },
  { nom: "Courses", c: "var(--vt-c-bleu)", centimes: 41236, moyenne: "≈ 400 €", tendance: "hausse" },
  { nom: "Transports", c: "var(--vt-c-terre)", centimes: 18690, moyenne: "≈ 190 €", tendance: "baisse" },
  { nom: "Loisirs", c: "var(--vt-c-violet)", centimes: 24315, moyenne: "≈ 250 €", tendance: "stable" },
] as const;

const FLECHE: Record<string, { d: string; libelle: string }> = {
  hausse: { d: "M7 17 17 7m0 0H9m8 0v8", libelle: "en hausse" },
  baisse: { d: "M7 7l10 10m0 0V9m0 8H9", libelle: "en baisse" },
  stable: { d: "M5 12h14m0 0-5-5m5 5-5 5", libelle: "stable" },
};

function CarteBudget() {
  const [formule, setFormule] = useState<"pro" | "light">("pro");
  const total = CATEGORIES.reduce((s, c) => s + c.centimes, 0);
  return (
    <Module nom="budget" acc="var(--vt-c-vert)" titre="Budget & vie quotidienne" question="Où part mon argent, chaque mois ?" i={0} className="md:col-span-2 lg:col-span-7 lg:row-span-2">
      {/* Pro ou Light : la même question, deux niveaux de détail. */}
      <div className="mt-6 inline-flex self-start p-1 rounded-full" style={{ background: "color-mix(in srgb, var(--vt-encre) 7%, transparent)" }} role="group" aria-label="Niveau de détail">
        {(["pro", "light"] as const).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFormule(f)}
            aria-pressed={formule === f}
            className="px-3.5 sm:px-4 py-1.5 rounded-full text-[12.5px] sm:text-[13px] font-semibold whitespace-nowrap transition-all duration-300"
            style={formule === f
              ? { background: "var(--vt-carte)", boxShadow: "0 1px 2px rgb(0 0 0 / .12), 0 4px 10px -4px rgb(0 0 0 / .25)" }
              : { color: "var(--vt-encre-2)" }}
          >
            <span className="hidden sm:inline">Gestic </span>{f === "pro" ? "Pro · au centime" : "Light · en tendances"}
          </button>
        ))}
      </div>

      <div key={formule} className="vt-bascule mt-5">
        <p className="text-[15px] vt-doux leading-relaxed max-w-lg">
          {formule === "pro"
            ? "Saisis chaque dépense et chaque revenu au centime, et fais-les matcher avec ton solde bancaire."
            : "Ou suis simplement tes moyennes et tes tendances, sans rien pointer. Cinq minutes par semaine."}
        </p>

        {/* Répartition par catégorie : barre empilée + étiquettes directes. */}
        <div className="mt-5 rounded-2xl p-4" style={{ background: "color-mix(in srgb, var(--vt-fond) 60%, transparent)", border: "1px solid var(--vt-bord)" }}>
          <div className="flex items-baseline justify-between gap-3">
            <p className="text-[11px] font-bold uppercase tracking-wider vt-doux">Septembre · par catégorie</p>
            <p className="text-[13px] font-semibold tabular-nums whitespace-nowrap">{formule === "pro" ? eur(total) : "≈ 1 660 € / mois"}</p>
          </div>
          <div className="mt-3 h-3.5 flex gap-[2px]" role="img" aria-label={CATEGORIES.map((c) => `${c.nom} ${formule === "pro" ? eur(c.centimes) : c.moyenne}`).join(", ")}>
            {CATEGORIES.map((c, i) => (
              <span
                key={c.nom}
                title={`${c.nom} · ${formule === "pro" ? eur(c.centimes) : c.moyenne}`}
                className={`vt-pousse h-full ${i === 0 ? "rounded-l-[4px]" : ""} ${i === CATEGORIES.length - 1 ? "rounded-r-[4px]" : ""}`}
                style={{ width: `${(c.centimes / total) * 100}%`, background: c.c, opacity: formule === "light" ? 0.55 : 1, "--i": i } as React.CSSProperties}
              />
            ))}
          </div>
          <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
            {CATEGORIES.map((c) => (
              <li key={c.nom} className="flex items-center justify-between gap-3 text-[13px]">
                <span className="flex items-center gap-2 min-w-0">
                  <span aria-hidden className="w-2.5 h-2.5 rounded-[3px] shrink-0" style={{ background: c.c }} />
                  <span className="truncate">{c.nom}</span>
                </span>
                {formule === "pro" ? (
                  <span className="font-semibold tabular-nums whitespace-nowrap">{eur(c.centimes)}</span>
                ) : (
                  <span className="flex items-center gap-1.5 font-semibold tabular-nums whitespace-nowrap">
                    {c.moyenne}
                    <svg className="w-3.5 h-3.5 vt-doux" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.4} role="img" aria-label={FLECHE[c.tendance].libelle}>
                      <path strokeLinecap="round" strokeLinejoin="round" d={FLECHE[c.tendance].d} />
                    </svg>
                  </span>
                )}
              </li>
            ))}
          </ul>
          {formule === "pro" ? (
            <p className="mt-4 flex items-center gap-2 text-[12.5px] font-semibold rounded-xl px-3 py-2" style={{ background: "color-mix(in srgb, var(--vt-vert) 12%, transparent)", color: "var(--vt-vert)" }}>
              <Coche />
              Solde calculé = solde de la banque, au centime
            </p>
          ) : (
            <p className="mt-4 text-[12.5px] vt-doux rounded-xl px-3 py-2" style={{ background: "color-mix(in srgb, var(--vt-encre) 5%, transparent)" }}>
              Moyenne des 3 derniers mois · recalage du solde quand tu veux
            </p>
          )}
        </div>
      </div>

      <div className="mt-auto pt-7">
        <p className="text-[11px] font-bold uppercase tracking-wider vt-doux">Et tout ce qui fait le quotidien</p>
        <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-4">
          {[
            { t: "Budgets & prévisionnel", d: "Des enveloppes par catégorie, et le mois prochain avant qu'il arrive." },
            { t: "Vie commune", d: "Dépenses partagées, parts pondérées, remboursements." },
            { t: "Tâches ménagères", d: "Qui fait quoi à la maison, sans post-it sur le frigo." },
            { t: "Journaux de vacances et week-ends", d: "En mode album : les photos et le récit de chaque séjour." },
            { t: "Organisation de projets", d: "Vacances ou autres : tout ce qu'il y a à préparer, au même endroit." },
          ].map((p) => (
            <li key={p.t} className="flex gap-2.5">
              <Coche />
              <span>
                <span className="block text-[14px] font-semibold">{p.t}</span>
                <span className="block mt-0.5 text-[13px] vt-doux leading-relaxed">{p.d}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </Module>
  );
}

function CartePatrimoine() {
  const financier = 38420;
  const corporel = 148030;
  const total = financier + corporel;
  const n = (v: number) => v.toLocaleString("fr-FR") + " €";
  return (
    <Module nom="patrimoine" acc="var(--vt-c-ambre)" titre="Patrimoine financier & corporel" question="Qu'est-ce que je possède, vraiment ?" i={1} className="lg:col-span-5">
      <p className="mt-4 text-[15px] vt-doux leading-relaxed">
        Livrets, PEE, placements… et tout ce qui a de la valeur : immobilier, matériel, collection de vinyles.
      </p>
      <div className="mt-5">
        <div className="flex items-baseline justify-between gap-3">
          <p className="text-[11px] font-bold uppercase tracking-wider vt-doux">Patrimoine global</p>
          <p className="vt-titre text-[28px] tabular-nums">{n(total)}</p>
        </div>
        <div className="mt-3 h-3.5 flex gap-[2px]" role="img" aria-label={`Financier ${n(financier)}, corporel ${n(corporel)}`}>
          <span className="vt-pousse h-full rounded-l-[4px]" style={{ width: `${(financier / total) * 100}%`, background: "var(--vt-c-vert)" }} />
          <span className="vt-pousse h-full rounded-r-[4px]" style={{ width: `${(corporel / total) * 100}%`, background: "var(--vt-c-ambre)", "--i": 1 } as React.CSSProperties} />
        </div>
        <div className="mt-3 grid grid-cols-2 gap-4 text-[13px]">
          <div>
            <p className="flex items-center gap-2"><span aria-hidden className="w-2.5 h-2.5 rounded-[3px]" style={{ background: "var(--vt-c-vert)" }} />Financier</p>
            <p className="mt-0.5 font-semibold tabular-nums">{n(financier)}</p>
          </div>
          <div>
            <p className="flex items-center gap-2"><span aria-hidden className="w-2.5 h-2.5 rounded-[3px]" style={{ background: "var(--vt-c-ambre)" }} />Corporel</p>
            <p className="mt-0.5 font-semibold tabular-nums">{n(corporel)}</p>
          </div>
        </div>
      </div>
      <p className="mt-auto pt-5 text-[15px] font-semibold flex items-start gap-2">
        <span aria-hidden style={{ color: "var(--vt-vert)" }}>→</span>
        Une vision complète de ton patrimoine global.
      </p>
    </Module>
  );
}

function CarteIntermittence() {
  const acquises = 412;
  const seuil = 507;
  const part = acquises / seuil;
  return (
    <Module nom="intermittence" acc="var(--vt-c-violet)" titre="Intermittence du spectacle" question="Il me manque combien d'heures pour mes 507 ?" i={2} className="lg:col-span-5">
      <div className="mt-5 flex items-center gap-5">
        <svg viewBox="0 0 88 88" className="w-[88px] h-[88px] shrink-0 -rotate-90" role="img" aria-label={`${acquises} heures sur ${seuil}`}>
          <circle cx="44" cy="44" r="36" fill="none" stroke="color-mix(in srgb, var(--vt-encre) 10%, transparent)" strokeWidth="8" />
          <circle className="vt-anneau" pathLength={1} cx="44" cy="44" r="36" fill="none" stroke="var(--vt-c-violet)" strokeWidth="8" strokeLinecap="round" style={{ "--fin": 1 - part } as React.CSSProperties} />
        </svg>
        <div>
          <p className="vt-titre text-[30px] tabular-nums">{acquises} h</p>
          <p className="text-[13px] vt-doux">sur {seuil} · encore {seuil - acquises} h</p>
        </div>
      </div>
      <ul className="mt-5 space-y-2 text-[14px]">
        {["Cachets et heures, par employeur", "Allocation journalière et date anniversaire", "Tes DSM de chaque mois", "Simulateur brut / net / coût employeur"].map((p) => (
          <li key={p} className="flex gap-2.5"><Coche /><span>{p}</span></li>
        ))}
      </ul>
    </Module>
  );
}

function CarteVoiture() {
  return (
    <Module nom="voiture" acc="var(--vt-c-bleu)" titre="Voiture" question="C'est quand, la prochaine vidange ?" i={3} className="lg:col-span-5">
      <ul className="mt-5 space-y-2">
        {[
          { t: "Vidange", d: "dans 1 200 km", urgent: true },
          { t: "Contrôle technique", d: "14 novembre", urgent: false },
          { t: "Consommation moyenne", d: "5,8 L / 100 km", urgent: false },
        ].map((e) => (
          <li key={e.t} className="flex items-center justify-between gap-3 rounded-xl px-3.5 py-2.5 text-[13.5px]" style={{ background: "color-mix(in srgb, var(--vt-fond) 60%, transparent)", border: "1px solid var(--vt-bord)" }}>
            <span className="font-semibold">{e.t}</span>
            <span className={`tabular-nums ${e.urgent ? "font-semibold" : "vt-doux"}`}>
              {e.urgent && <span aria-hidden className="inline-block w-1.5 h-1.5 rounded-full mr-2 align-middle" style={{ background: "var(--vt-c-bleu)" }} />}
              {e.d}
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-5 text-[14px] vt-doux leading-relaxed">
        Entretiens et rappels, factures rangées, kilométrage et consommation, suivi des km pour l&apos;assurance. Les échéances se glissent toutes seules dans tes budgets à venir.
      </p>
    </Module>
  );
}

/** Un petit arbre qui se dessine ; l'ancêtre inconnu est en pointillés. */
function MiniArbre() {
  const noeud = (x: number, y: number, t: string) => (
    <g key={t + x}>
      <circle cx={x} cy={y} r="13" fill="var(--vt-carte)" stroke="var(--vt-c-terre)" strokeWidth="2" />
      <text x={x} y={y + 3.5} textAnchor="middle" fontSize="10" fontWeight="600" fill="var(--vt-encre)">{t}</text>
    </g>
  );
  const trait = { fill: "none", stroke: "var(--vt-encre-2)", strokeWidth: 1.5, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, opacity: 0.6 };
  return (
    <svg viewBox="38 0 196 168" className="w-full max-w-[300px]" aria-hidden>
      {/* Fratrie : l'ancêtre connu et le frère qu'on cherche. */}
      <path className="vt-trace" pathLength={1} d="M58 12V6H112V13" {...trait} />
      <path className="vt-trace" pathLength={1} d="M125 26H151M138 26V84" {...trait} />
      <path className="vt-trace vt-trace-2" pathLength={1} d="M151 97H177M164 97V122M136 122H216M136 122V144M216 122V144" {...trait} />
      {noeud(112, 26, "JB")}
      {noeud(164, 26, "MA")}
      {noeud(138, 97, "LP")}
      {noeud(190, 97, "CD")}
      {noeud(136, 150, "Toi")}
      {noeud(216, 150, "EL")}
      <g>
        <circle cx="58" cy="26" r="13" fill="none" stroke="var(--vt-c-terre)" strokeWidth="2" strokeDasharray="2 4" strokeLinecap="round" />
        <text x="58" y="30.5" textAnchor="middle" fontSize="13" fontWeight="700" fill="var(--vt-c-terre)">?</text>
      </g>
    </svg>
  );
}

function CarteFamille() {
  return (
    <Module nom="famille" acc="var(--vt-c-terre)" titre="Famille & arbre généalogique" question="C'était qui, le frère de mon arrière-grand-mère ?" i={4} className="md:col-span-2 lg:col-span-7">
      <div className="mt-5 grid sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-6 items-center">
        <div>
          <p className="vt-titre text-[24px] leading-tight">La meilleure expérience d&apos;arbre généalogique qui existe.</p>
          <ul className="mt-4 space-y-2 text-[14px]">
            {["Placement automatique, même pour les familles recomposées", "Photos, lieux, métiers, souvenirs", "Partage simple, en un lien", "Collaboration : chacun complète sa branche"].map((p) => (
              <li key={p} className="flex gap-2.5"><Coche /><span>{p}</span></li>
            ))}
          </ul>
        </div>
        <div className="flex justify-center"><MiniArbre /></div>
      </div>
    </Module>
  );
}

function CarteAVenir() {
  return (
    <article className="vt-monte vt-avenir md:col-span-2 lg:col-span-12 p-6 sm:p-8" style={{ "--i": 5 } as React.CSSProperties}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="vt-sur">En pointillés, comme tout ce qui est prévu</p>
          <h3 className="vt-titre text-[30px] sm:text-[34px] mt-2">Bientôt dans Gestic.</h3>
        </div>
        <p className="text-[14px] vt-doux max-w-sm">Les prochains modules sont déjà sur l&apos;établi. Comme le reste, je les construis d&apos;abord pour moi.</p>
      </div>
      <div className="mt-7 grid sm:grid-cols-3 gap-4">
        {[
          { nom: "music" as const, t: "Music", d: "La gestion de groupes : répétitions, setlists, matériel, concerts." },
          { nom: "clients" as const, t: "Portefeuille clients", d: "En mieux : prestations, abonnements, fournisseurs et stock." },
          { nom: "velo" as const, t: "Vélos", d: "Entretien et suivi de tes vélos, comme pour la voiture." },
        ].map((m) => (
          <div key={m.t} className="rounded-2xl p-5" style={{ border: "1px dashed var(--vt-bord)", "--acc": "var(--vt-vert)" } as React.CSSProperties}>
            <span className="vt-icone"><Icone nom={m.nom} /></span>
            <p className="mt-3 text-[16px] font-semibold">{m.t}</p>
            <p className="mt-1 text-[14px] vt-doux leading-relaxed">{m.d}</p>
          </div>
        ))}
      </div>
    </article>
  );
}

/* ══════════════════════════════════════════
   Page
   ══════════════════════════════════════════ */
export function Vitrine() {
  const pro = GESTIC_PLANS.pro;
  const fraisPro = stripeFeesCents(pro.priceCents);

  return (
    <div className="vt min-h-screen">
      <style>{CSS}</style>

      {/* ── Nav ── */}
      <header className="sticky top-0 z-50 backdrop-blur-xl" style={{ background: "color-mix(in srgb, var(--vt-fond) 82%, transparent)", borderBottom: "1px solid var(--vt-bord)" }}>
        <div className="mx-auto max-w-6xl px-5 sm:px-8 h-16 flex items-center justify-between gap-6">
          <a href="#histoire" className="flex items-center gap-2.5 shrink-0" aria-label="Gestic, retour en haut">
            <Logo />
            <span className="vt-titre text-[22px]">{LANDING.nav.brand}</span>
          </a>
          <nav className="hidden md:flex items-center gap-7 text-[14px] vt-doux">
            {LANDING.nav.links.map((l) => (
              <a key={l.href} href={l.href} className="hover:opacity-70 transition-opacity">{l.label}</a>
            ))}
          </nav>
          <div className="flex items-center gap-2.5 shrink-0">
            {/* Sur un écran de téléphone, la barre ne tient qu'avec un seul appel. */}
            <a href={LANDING.nav.ctaSecondary.href} className="hidden sm:inline-block text-[14px] font-medium px-3 py-2 vt-doux hover:opacity-70 transition-opacity">{LANDING.nav.ctaSecondary.label}</a>
            <a href="#pricing" className="vt-bouton !px-4 sm:!px-5 !py-2.5 !text-[14px]">
              <span className="sm:hidden">Essayer</span>
              <span className="hidden sm:inline">Essayer 14 jours</span>
            </a>
          </div>
        </div>
      </header>

      <Hero />

      {/* ── Les modules : une question, une carte ── */}
      <Section id="modules" fond>
        <div className="max-w-3xl">
          <p className="vt-sur">Tout au même endroit</p>
          <h2 className="vt-titre text-4xl sm:text-[54px] mt-3">Chaque question sans réponse est devenue un module.</h2>
          <p className="mt-5 text-[17px] vt-doux leading-relaxed max-w-2xl">
            J&apos;ai commencé par mon budget. Puis j&apos;ai voulu voir le reste : ce que je possède, mes heures d&apos;intermittent, ma voiture, ma famille. Tout se parle, rien n&apos;est saisi deux fois.
          </p>
        </div>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-12 gap-5">
          <CarteBudget />
          <CartePatrimoine />
          <CarteIntermittence />
          <CarteVoiture />
          <CarteFamille />
          <CarteAVenir />
        </div>
      </Section>

      {/* ── Pourquoi c'est différent ── */}
      <Section>
        <div className="grid gap-12">
          <div>
            <p className="vt-sur">Pourquoi je la fais comme ça</p>
            <h2 className="vt-titre text-4xl sm:text-5xl mt-3 max-w-3xl">Pas une start-up. L&apos;app que j&apos;utilise tous les jours.</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { chiffre: "0", libelle: "publicité, pistage, revente de données", detail: "Ton abonnement est le seul revenu de Gestic." },
              { chiffre: "0", libelle: "connexion à ta banque", detail: "Tu saisis ce que tu veux, quand tu veux." },
              { chiffre: "1", libelle: "personne derrière, en France", detail: "C'est moi qui code, et moi qui réponds." },
            ].map((s, i) => (
              <div key={s.libelle} className="vt-monte" style={{ "--i": i } as React.CSSProperties}>
                <p className="vt-titre text-6xl" style={{ color: "var(--vt-vert)" }}>{s.chiffre}</p>
                <p className="mt-2 text-[15px] font-semibold">{s.libelle}</p>
                <p className="mt-1 text-[13px] vt-doux leading-relaxed">{s.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── Les deux formules ── */}
      <Section id="pricing" fond>
        <div className="text-center max-w-2xl mx-auto">
          <p className="vt-sur">{LANDING.pricing.eyebrow}</p>
          <h2 className="vt-titre text-4xl sm:text-5xl mt-3">Deux formules, zéro piège.</h2>
          <p className="mt-4 text-[17px] vt-doux leading-relaxed">Même moteur, même sérieux. Au centime ou en tendances : à toi de voir le niveau de détail que tu veux tenir.</p>
        </div>
        <div className="mt-12 grid md:grid-cols-2 gap-6 items-start">
          {LANDING.pricing.plans.map((plan, i) => {
            const mise = plan.featured;
            return (
              <div
                key={plan.id}
                id={plan.id}
                className="vt-monte relative rounded-[26px] p-7 scroll-mt-24"
                style={{
                  "--i": i,
                  background: mise ? "linear-gradient(180deg, color-mix(in srgb, var(--vt-vert) 12%, var(--vt-carte)), var(--vt-carte))" : "var(--vt-carte)",
                  border: mise ? "1.5px solid color-mix(in srgb, var(--vt-vert) 45%, transparent)" : "1px solid var(--vt-bord)",
                  boxShadow: mise ? "var(--vt-ombre)" : "none",
                } as React.CSSProperties}
              >
                {mise && (
                  <span className="absolute -top-3 left-7 text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full" style={{ background: "var(--vt-vert)", color: "#FBF6EA" }}>
                    Le plus complet
                  </span>
                )}
                <p className="vt-titre text-2xl">{plan.name}</p>
                <p className="mt-1.5 text-[14px] vt-doux">{plan.tagline}</p>
                <p className="mt-6 flex items-baseline gap-1.5">
                  <span className="vt-titre text-5xl">{eur(GESTIC_PLANS[plan.checkoutPlan].priceCents)}</span>
                  <span className="text-[15px] vt-doux">/ mois</span>
                </p>
                <ul className="mt-6 space-y-2.5">
                  {plan.features.map((f) => (
                    <li key={f} className="flex gap-2.5 text-[14px]"><Coche /><span>{f}</span></li>
                  ))}
                </ul>
                <BoutonEssai
                  plan={plan.checkoutPlan}
                  libelle={plan.cta.label}
                  className={`mt-7 w-full justify-center ${mise ? "vt-bouton" : "vt-bouton-2"}`}
                />
                <p className="mt-3 text-[12px] text-center vt-doux leading-snug">{plan.trialNote}</p>
              </div>
            );
          })}
        </div>
      </Section>

      {/* ── Transparence ── */}
      <Section id="transparence">
        <div className="max-w-2xl">
          <p className="vt-sur">{LANDING.transparence.eyebrow}</p>
          <h2 className="vt-titre text-4xl sm:text-5xl mt-3">Où va ton argent, exactement.</h2>
          <p className="mt-4 text-[17px] vt-doux leading-relaxed">
            Sur {eur(pro.priceCents)} d&apos;abonnement Pro, {eur(fraisPro)}{" "}
            partent en frais de paiement. Le reste paie l&apos;hébergement et mon temps. Rien d&apos;autre : pas de pub, pas d&apos;investisseurs à rembourser.
          </p>
        </div>
        <div className="vt-monte mt-10 rounded-[26px] p-6 sm:p-8 vt-carte">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <p className="vt-titre text-3xl">{eur(pro.priceCents)}</p>
            <p className="text-[13px] vt-doux">décompte d&apos;un mois d&apos;abonnement Pro</p>
          </div>
          <div className="mt-5 h-4 rounded-full overflow-hidden flex gap-[3px]" style={{ background: "color-mix(in srgb, var(--vt-encre) 8%, transparent)" }} aria-hidden>
            <span style={{ width: `${(fraisPro / pro.priceCents) * 100}%`, background: "var(--vt-terre)" }} />
            <span style={{ width: `${((pro.priceCents - fraisPro) / pro.priceCents) * 0.35 * 100}%`, background: "var(--vt-sauge)" }} />
            <span className="flex-1" style={{ background: "var(--vt-vert)" }} />
          </div>
          <div className="mt-4 grid sm:grid-cols-3 gap-4 text-[13px]">
            {[
              { c: "var(--vt-terre)", t: "Frais de paiement", v: eur(fraisPro), d: "Stripe : 1,5 % + 0,25 € + 0,7 % d'abonnement" },
              { c: "var(--vt-sauge)", t: "Hébergement", v: "≈ " + eur(Math.round((pro.priceCents - fraisPro) * 0.35)), d: "Serveurs, base de données, sauvegardes" },
              { c: "var(--vt-vert)", t: "Mon temps", v: "le reste", d: "Développement, support, corrections" },
            ].map((l) => (
              <div key={l.t}>
                <p className="flex items-center gap-2 font-semibold">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: l.c }} aria-hidden />
                  {l.t}
                </p>
                <p className="mt-1 tabular-nums font-semibold">{l.v}</p>
                <p className="mt-0.5 vt-doux leading-relaxed">{l.d}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── FAQ ── */}
      <Section fond>
        <div className="grid lg:grid-cols-[minmax(0,.9fr)_minmax(0,1.1fr)] gap-10">
          <div>
            <p className="vt-sur">{LANDING.faq.eyebrow}</p>
            <h2 className="vt-titre text-4xl mt-3">Tout ce qu&apos;il faut savoir.</h2>
            <p className="mt-4 text-[15px] vt-doux leading-relaxed">Une question qui n&apos;est pas là ? Écris-moi, c&apos;est moi qui réponds.</p>
          </div>
          <div className="divide-y" style={{ borderColor: "var(--vt-bord)" }}>
            {LANDING.faq.items.map((item, i) => <Question key={item.q} q={item.q} r={item.a} i={i} />)}
          </div>
        </div>
      </Section>

      {/* ── Bandeau final ── */}
      <section className="relative overflow-hidden px-5 sm:px-8 py-24" style={{ background: "var(--vt-vert)" }}>
        <span aria-hidden className="vt-lueur" style={{ width: 420, height: 420, background: "#52B788", top: -160, left: "12%" }} />
        <div className="relative mx-auto max-w-4xl text-center" style={{ color: "#FBF6EA" }}>
          <p className="vt-recit text-[22px] sm:text-[26px]" style={{ color: "color-mix(in srgb, #FBF6EA 78%, transparent)" }}>Moi, j&apos;ai retrouvé la vision.</p>
          <h2 className="vt-titre text-4xl sm:text-6xl mt-3">À ton tour de voir clair.</h2>
          <p className="mt-5 text-[17px]" style={{ color: "color-mix(in srgb, #FBF6EA 80%, transparent)" }}>
            14 jours d&apos;essai. Carte demandée, débitée au 15ᵉ jour seulement. Résiliation en deux clics, sans justification.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <a href="#pricing" className="vt-bouton" style={{ background: "#FBF6EA", color: "var(--vt-vert)" }}>Choisir ma formule</a>
            <a href="/login" className="vt-bouton-2" style={{ color: "#FBF6EA", borderColor: "color-mix(in srgb, #FBF6EA 40%, transparent)" }}>J&apos;ai déjà un compte</a>
          </div>
        </div>
      </section>

      {/* ── Pied de page ── */}
      <footer className="px-5 sm:px-8 py-12" style={{ borderTop: "1px solid var(--vt-bord)" }}>
        <div className="mx-auto max-w-6xl flex flex-wrap gap-8 justify-between">
          <div className="max-w-xs">
            <p className="flex items-center gap-2.5">
              <Logo taille={32} />
              <span className="vt-titre text-xl">{LANDING.footer.brand}</span>
            </p>
            <p className="mt-3 text-[13px] vt-doux leading-relaxed">{LANDING.footer.tagline}</p>
          </div>
          {LANDING.footer.columns.map((c) => (
            <div key={c.title}>
              <p className="text-[12px] font-bold uppercase tracking-wider vt-doux">{c.title}</p>
              <ul className="mt-3 space-y-2 text-[14px]">
                {c.links.map((l) => <li key={l.label}><a href={l.href} className="hover:opacity-70 transition-opacity">{l.label}</a></li>)}
              </ul>
            </div>
          ))}
        </div>
        <div className="mx-auto max-w-6xl mt-10 pt-6 flex flex-wrap items-center justify-between gap-3 text-[12px] vt-doux" style={{ borderTop: "1px solid var(--vt-bord)" }}>
          <p>{LANDING.footer.bottomLine}</p>
          <p className="tabular-nums">v{APP_VERSION}</p>
        </div>
      </footer>
    </div>
  );
}

function Question({ q, r, i }: { q: string; r: string; i: number }) {
  const [ouvert, setOuvert] = useState(i === 0);
  return (
    <div className="vt-monte" style={{ "--i": i } as React.CSSProperties}>
      <button
        type="button"
        onClick={() => setOuvert((v) => !v)}
        aria-expanded={ouvert}
        className="w-full flex items-center justify-between gap-4 py-4 text-left"
      >
        <span className="text-[16px] font-semibold">{q}</span>
        <svg className={`w-4 h-4 shrink-0 transition-transform duration-300 ${ouvert ? "rotate-180" : ""}`} style={{ color: "var(--vt-vert)" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      <div className="grid transition-[grid-template-rows] duration-300 ease-out" style={{ gridTemplateRows: ouvert ? "1fr" : "0fr" }}>
        <div className="overflow-hidden">
          <p className="pb-4 text-[15px] vt-doux leading-relaxed">{r}</p>
        </div>
      </div>
    </div>
  );
}
